/**
 * React Query hooks for the Foundation module's HTTP surface (P7+).
 *
 * Mirrors backend/foundation/mvb_router.py + the MVB admin CRUD
 * + state-machine endpoints under /v1/admin/foundation/mvb-
 * declarations/*.
 *
 * Module-boundary rule: this file may import from `foundation/*` +
 * `shared/*` only -- no twin/, no framework/. Mirrors
 * frontend/twin/api/hooks.ts.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import { mvbKeys } from "shared/api/queryClient";
import type {
  MvbDeclaration,
  MvbScorecard,
  MvbScorecardNone,
  MvbSignOffRole,
  MvbStatus,
} from "shared/api/types";

// -- Public reads --------------------------------------------------

/**
 * Subscribe to the currently-active approved MVB declaration.
 *
 * 404 from the backend surfaces as ``ApiError`` on .error -- caller
 * renders an empty state ("no active declaration"). Polled every
 * 60s because the active-window check is date-based + the
 * declaration may flip on day boundaries.
 */
export function useActiveMvbDeclaration(): UseQueryResult<MvbDeclaration, ApiError> {
  return useQuery({
    queryKey: mvbKeys.active(),
    queryFn: () => api.get<MvbDeclaration>("/v1/mvb/active"),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Subscribe to the MVB scorecard composite shape.
 *
 * 404 when no active declaration -- caller renders an empty state.
 */
export function useMvbScorecard(): UseQueryResult<
  MvbScorecard | MvbScorecardNone,
  ApiError
> {
  return useQuery({
    queryKey: mvbKeys.scorecard(),
    queryFn: () => api.get<MvbScorecard | MvbScorecardNone>("/v1/mvb/scorecard"),
    staleTime: 60_000,
    retry: false,
  });
}

// -- Admin CRUD + state machine ------------------------------------

export interface UseMvbDeclarationsListOptions {
  status?: MvbStatus | undefined;
}

export function useMvbDeclarations(
  opts: UseMvbDeclarationsListOptions = {},
): UseQueryResult<MvbDeclaration[], ApiError> {
  const path = opts.status
    ? `/v1/admin/foundation/mvb-declarations?status=${encodeURIComponent(opts.status)}`
    : "/v1/admin/foundation/mvb-declarations";
  return useQuery({
    queryKey: mvbKeys.list({ status: opts.status }),
    queryFn: () => api.get<MvbDeclaration[]>(path),
    staleTime: 30_000,
  });
}

export function useMvbDeclaration(
  id: string | undefined,
): UseQueryResult<MvbDeclaration, ApiError> {
  return useQuery({
    queryKey: id ? mvbKeys.detail(id) : [...mvbKeys.all, "detail", "_disabled"],
    queryFn: () =>
      api.get<MvbDeclaration>(`/v1/admin/foundation/mvb-declarations/${id}`),
    enabled: !!id,
    staleTime: 30_000,
    // Round 14 C3 -- the CRO's page still read "Waiting on the
    // Accountable executive" after both had signed: the other signature
    // lands in somebody else's session. An open declaration is re-read.
    refetchInterval: (q) => (q.state.data?.status === "open" ? 15_000 : false),
  });
}

/**
 * B3 -- what signing this declaration would put in force.
 *
 * The membership only appeared once a declaration was APPROVED, because
 * the snapshot is frozen at approval -- so the one moment the CRO could
 * see what she was signing was after her signature made it binding.
 * Served by the same composer the freeze uses, so the preview cannot
 * promise something the freeze then does differently.
 */
export function useMvbDeclarationPreview(
  id: string | undefined,
  enabled: boolean,
): UseQueryResult<Record<string, unknown>, ApiError> {
  return useQuery({
    queryKey: id
      ? [...mvbKeys.detail(id), "preview"]
      : [...mvbKeys.all, "preview", "_disabled"],
    queryFn: () =>
      api.get<Record<string, unknown>>(
        `/v1/admin/foundation/mvb-declarations/${id}/preview`,
      ),
    enabled: !!id && enabled,
    staleTime: 30_000,
  });
}

export interface MvbDeclarationCreateBody {
  name: string;
  description?: string | null;
  rationale?: string | null;
}

export function useCreateMvbDeclaration(): UseMutationResult<
  MvbDeclaration,
  ApiError,
  MvbDeclarationCreateBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.post<MvbDeclaration>("/v1/admin/foundation/mvb-declarations", body),
    // Round 14 C3 -- on a refusal too: a 409 means the record moved
    // under this page, and a control left live over the old state
    // invites the same refused press again.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: mvbKeys.all });
    },
  });
}

export function useSubmitMvbDeclaration(): UseMutationResult<
  MvbDeclaration,
  ApiError,
  // D6 (round 8) -- the reason the set is being put forward. The
  // endpoint has accepted and kept it since C2; nothing sent one, so
  // every submission recorded an absence.
  { id: string; note?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }) =>
      api.post<MvbDeclaration>(
        `/v1/admin/foundation/mvb-declarations/${id}/submit`,
        note ? { note } : {},
      ),
    // Round 16 G11 -- after "Declaration submitted" the page still read
    // "Draft" with Submit live: the refetch can read the record before
    // the submission is visible to it. The server answers with the
    // submitted declaration, so the page shows that at once and the
    // refetch below only confirms it.
    onSuccess: (decl) => {
      qc.setQueryData(mvbKeys.detail(decl.id), decl);
    },
    // Round 14 C3 -- refresh on a refusal as well as a success.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: mvbKeys.all });
    },
  });
}

/**
 * Round 14 C5 -- delete a declaration that has never been in force.
 *
 * Two refusals told a person to "delete it" and no screen offered the
 * verb; the endpoint existed and only a database session could reach
 * the record's way out.
 */
export function useDeleteMvbDeclaration(): UseMutationResult<
  void,
  ApiError,
  { id: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) =>
      api.delete<void>(`/v1/admin/foundation/mvb-declarations/${id}`),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: mvbKeys.all });
    },
  });
}

/** Round 14 C5 -- a signer takes their own signature back. */
export function useWithdrawMvbSignature(): UseMutationResult<
  MvbDeclaration,
  ApiError,
  { id: string; role: MvbSignOffRole }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }) =>
      api.post<MvbDeclaration>(
        `/v1/admin/foundation/mvb-declarations/${id}/withdraw-signature?role=${role}`,
        {},
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: mvbKeys.all });
    },
  });
}

export function useSignOffMvbDeclaration(): UseMutationResult<
  MvbDeclaration,
  ApiError,
  // Round 9 C5: the endpoint has taken a `note` since A4 and the client
  // never sent one, so the record read "Signed without a comment" under
  // three names when no box had ever been offered.
  { id: string; role: MvbSignOffRole; note?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role, note }) =>
      api.post<MvbDeclaration>(
        `/v1/admin/foundation/mvb-declarations/${id}/sign-off?role=${role}`,
        note ? { note } : {},
      ),
    // Round 14 C3 -- refresh on a refusal as well as a success.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: mvbKeys.all });
    },
  });
}
